import { HTMLAttributes, useEffect, useState } from 'react'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Form from '@/components/base/form'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { contactRevealAnimation } from '@/components/sections/animations/contact'
import Spacer from '@/components/sections/spacer'
import { useStore } from '@/contexts/store'
import { useLocationCascadeFields } from '@/hooks/use-location-cascade-fields'

import { WPGravityForm } from '@/types'

type FormTab = {
  label: string
  form: WPGravityForm
}

export type ContactsProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    tabs: FormTab[]
  }>

const Contacts = ({ title, tabs = [], ...props }: ContactsProps) => {
  const [activeTab, setActiveTab] = useState(0)
  const { strings } = useStore()

  const activeForm = tabs[activeTab]?.form
  const fields = activeForm?.fields || []

  const {
    enhancedFields,
    handleFieldChange,
    selectDependencies,
    resetSelection
  } = useLocationCascadeFields(fields, strings?.labels?.country_region_province)

  useEffect(() => {
    resetSelection()
  }, [resetSelection, activeTab])

  return (
    <Motion animation={contactRevealAnimation}>
      {(motionRef) => (
        <Section ref={motionRef} {...props}>
          <Spacer variant="xxl" />

          <Container>
            <Grid className="gap-y-spacing-lg">
              <div className="col-span-2 md:col-span-6 lg:col-span-12">
                {title ? (
                  <Typography
                    variant="h2"
                    component="h1"
                    className="text-text-accent-light dark:text-text-accent-dark font-medium"
                  >
                    {title}
                  </Typography>
                ) : null}
              </div>
            </Grid>
          </Container>

          <Spacer variant="sm" />

          <Container>
            <Grid className="gap-y-spacing-lg">
              {/* Colonna sinistra - Titolo e bottoni */}
              <div
                className="col-span-2 md:col-span-6 lg:col-span-4"
                data-motion-column
              >
                {tabs.length > 1 ? (
                  <div className="grid grid-cols-2 gap-spacing-2xs">
                    {tabs.map((tab, index) => (
                      <Button
                        key={index}
                        variant={activeTab === index ? 'primary' : 'secondary'}
                        onClick={() => setActiveTab(index)}
                        aria-pressed={activeTab === index}
                        className="justify-between px-[1.6rem] py-[1.8rem]"
                        endIcon={
                          <SVGIcon
                            symbol="cta-arrow"
                            aria-hidden="true"
                            className="ml-[1.6rem] size-[1.6rem] rotate-45"
                          />
                        }
                      >
                        <Typography variant="h6" component="span">
                          {tab.label}
                        </Typography>
                      </Button>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Colonna destra - Form */}
              <div
                className="col-span-2 md:col-span-6 lg:col-span-7 lg:col-start-6"
                data-motion-column
              >
                {activeForm?.id ? (
                  <Form
                    key={activeForm.id}
                    form_id={activeForm.id}
                    fields={enhancedFields}
                    submit={activeForm.button?.text}
                    selectDependencies={selectDependencies}
                    onFieldChange={handleFieldChange}
                  />
                ) : null}
              </div>
            </Grid>
          </Container>

          <Spacer variant="xl" />
        </Section>
      )}
    </Motion>
  )
}

export default Contacts
